import api from '../../services/api';
import { Product, ProductVariant } from '../../types/pz';
import { invalidateProductSelectionCatalog } from './productSelectionApi';

export interface ProductListFilters {
  search?: string;
  status?: 'ACTIVE' | 'INACTIVE' | null;
  category?: string | null;
  productCategoryId?: string | null;
}

export interface ProductFormValues {
  code?: string;
  name: string;
  englishName?: string | null;
  ean?: string | null;
  description?: string | null;
  category?: string | string[];
  productCategoryId?: string | null;
  cnCode?: string | null;
  countryOfOrigin?: string | null;
  netWeight?: number | null;
  grossWeight?: number | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  // Package-based LDM (logistics) defaults (migration 093). mm for dims.
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  averagePurchasePricePLN?: number | null;
  averagePurchasePriceUnit?: 'pcs' | 'm2' | 'm3' | null;
  defaultPurchasePrice?: number | null;
  defaultPurchaseCurrency?: string;
  defaultSalePrice?: number | null;
  defaultSaleCurrency?: string;
  status?: 'ACTIVE' | 'INACTIVE';
}

export interface VariantFormValues {
  ean?: string | null;
  attributes: Record<string, string>;
  netWeight?: number | null;
  grossWeight?: number | null;
  piecesPerPackage?: number | null;
  packageLength?: number | null;
  packageWidth?: number | null;
  packageStackLayers?: number | null;
  defaultPurchasePrice?: number | null;
  defaultPurchaseCurrency?: string;
  defaultSalePrice?: number | null;
  defaultSaleCurrency?: string;
  isActive?: boolean;
}

// Exported so buildProductPayload's whitelist can be unit-tested directly
// (createProduct/updateProduct wrap it, but the whitelist is the load-bearing
// contract — a field missing here is silently dropped from every save,
// regardless of what the Form.Item displays).
export function buildProductPayload(values: ProductFormValues) {
  return {
    code: values.code || undefined,
    name: values.name,
    englishName: values.englishName || null,
    ean: values.ean || null,
    description: values.description,
    category: Array.isArray(values.category) ? values.category[0] : values.category,
    productCategoryId: values.productCategoryId ?? null,
    cnCode: values.cnCode || null,
    countryOfOrigin: values.countryOfOrigin || null,
    netWeight: values.netWeight ?? null,
    grossWeight: values.grossWeight ?? null,
    width: values.width ?? null,
    height: values.height ?? null,
    length: values.length ?? null,
    thickness: values.thickness ?? null,
    // Package-based LDM (logistics) defaults. Without these keys the payload
    // silently dropped every value the operator typed into the "Pakowanie"
    // group (browser-regression HIGH-1) even though the form displayed the
    // fetched values correctly on load.
    piecesPerPackage: values.piecesPerPackage ?? null,
    packageLength: values.packageLength ?? null,
    packageWidth: values.packageWidth ?? null,
    packageStackLayers: values.packageStackLayers ?? null,
    ...(values.averagePurchasePricePLN !== undefined || values.averagePurchasePriceUnit !== undefined ? {
      averagePurchasePricePLN: values.averagePurchasePricePLN ?? null,
      averagePurchasePriceUnit: values.averagePurchasePricePLN == null ? null : values.averagePurchasePriceUnit,
    } : {}),
    defaultPurchasePrice: values.defaultPurchasePrice ?? null,
    defaultPurchaseCurrency: values.defaultPurchaseCurrency || 'USD',
    defaultSalePrice: values.defaultSalePrice ?? null,
    defaultSaleCurrency: values.defaultSaleCurrency || 'PLN',
    isActive: values.status !== 'INACTIVE',
  };
}

export function buildVariantPayload(productId: string, values: VariantFormValues) {
  return {
    productId,
    ean: values.ean || null,
    attributes: values.attributes,
    netWeight: values.netWeight ?? null,
    grossWeight: values.grossWeight ?? null,
    piecesPerPackage: values.piecesPerPackage ?? null,
    packageLength: values.packageLength ?? null,
    packageWidth: values.packageWidth ?? null,
    packageStackLayers: values.packageStackLayers ?? null,
    defaultPurchasePrice: values.defaultPurchasePrice ?? null,
    defaultPurchaseCurrency: values.defaultPurchaseCurrency || 'USD',
    defaultSalePrice: values.defaultSalePrice ?? null,
    defaultSaleCurrency: values.defaultSaleCurrency || 'PLN',
    isActive: values.isActive !== false,
  };
}

export async function fetchProducts(filters: ProductListFilters = {}) {
  const params = new URLSearchParams({ includeVariants: 'true' });
  if (filters.search) params.set('search', filters.search);
  if (filters.category) params.set('category', filters.category);
  if (filters.productCategoryId) params.set('productCategoryId', filters.productCategoryId);
  if (filters.status) params.set('isActive', String(filters.status === 'ACTIVE'));

  const response = await api.get<Product[]>(`/products?${params.toString()}`);
  return response.data;
}

export async function fetchProduct(id: string) {
  const response = await api.get<Product>(`/products/${id}?includeVariants=true`);
  return response.data;
}

/**
 * Returns the FIXED enum classification values (RAW_MATERIAL/FINISHED_GOOD/…),
 * shown in the UI as "Klasyfikacja". This is NOT the user-managed taxonomy — for
 * that ("Kategoria": Płyta OSB, Regips…) use
 * `fetchProductCategoryTags` in ./productCategoriesApi.
 */
export async function fetchProductCategories() {
  const response = await api.get<string[]>('/products/categories');
  return response.data;
}

export async function createProduct(values: ProductFormValues) {
  const response = await api.post<Product>('/products', buildProductPayload(values));
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function updateProduct(id: string, values: ProductFormValues) {
  const response = await api.put<Product>(`/products/${id}`, buildProductPayload(values));
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function deleteProduct(id: string) {
  await api.delete(`/products/${id}`);
  invalidateProductSelectionCatalog();
}

export async function permanentlyDeleteProduct(id: string) {
  await api.delete(`/products/${id}/permanent`);
  invalidateProductSelectionCatalog();
}

export async function createVariant(productId: string, values: VariantFormValues) {
  const response = await api.post<ProductVariant>(
    `/products/${productId}/variants`,
    buildVariantPayload(productId, values)
  );
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function updateVariant(productId: string, variantId: string, values: VariantFormValues) {
  const response = await api.put<ProductVariant>(
    `/products/variants/${variantId}`,
    buildVariantPayload(productId, values)
  );
  invalidateProductSelectionCatalog();
  return response.data;
}

export async function deleteVariant(variantId: string) {
  await api.delete(`/products/variants/${variantId}`);
  invalidateProductSelectionCatalog();
}
